import React from "react";
import styled, { css } from "styled-components";
import { TOS_DOCUMENT_META, TOS_PREAMBLE, TOS_PREAMBLE_NOTICE, TOS_SECTIONS, TosNode } from "./termsOfServiceContent";

const TermsContainer = styled.div<{ $fullPage: boolean; $printMode: boolean }>`
	max-height: ${({ $fullPage, $printMode }) => ($fullPage || $printMode ? "none" : "60vh")};
	overflow-y: ${({ $fullPage, $printMode }) => ($fullPage || $printMode ? "visible" : "auto")};
	padding: 0;

	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-thumb {
		background: #d9d9d9;
		border-radius: 4px;
	}

	&::-webkit-scrollbar-thumb:hover {
		background: #bfbfbf;
	}

	${({ $printMode }) =>
		$printMode &&
		css`
			width: 100%;
		`}

	@media print {
		max-height: none;
		overflow: visible;
		padding: 0;
	}
`;

const Document = styled.article<{ $embedded: boolean }>`
	box-sizing: border-box;
	width: 100%;
	padding: ${({ $embedded }) => ($embedded ? "32px" : "80px")};
	display: flex;
	flex-direction: column;
	gap: 40px;
	background: ${({ $embedded }) => ($embedded ? "transparent" : "#f3f5ff")};
	color: #303030;
	font-family: Poppins, sans-serif;
	font-size: 14px;
	line-height: 24px;
	word-break: break-word;

	* {
		box-sizing: border-box;
	}

	@media (max-width: 700px) {
		padding: ${({ $embedded }) => ($embedded ? "20px 16px" : "32px 20px")};
		gap: 32px;
	}
`;

const DocumentHeader = styled.header`
	padding-bottom: 24px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 24px;
`;

const Logo = styled.img`
	display: block;
	width: 240px;
	height: auto;
	max-width: 65%;
`;

const Eyebrow = styled.span`
	color: #6936dd;
	font-size: 12px;
	font-weight: 500;
	white-space: nowrap;
`;

const Preamble = styled.section`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const PreambleHeading = styled.h2`
	margin: 0;
	color: #3a0ca3;
	font-size: 14px;
	font-weight: 600;
	line-height: 24px;
`;

const PreambleParagraph = styled.p`
	margin: 0;
`;

const Notice = styled.aside<{ $embedded: boolean }>`
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding: 20px;
	border-radius: 12px;
	background: ${({ $embedded }) => ($embedded ? "#dde2fa" : "#ebeefd")};

	p {
		margin: 0;
	}

	p:last-child {
		color: #707070;
	}
`;

const NoticeLabel = styled.u`
	font-weight: 600;
`;

const TitleBlock = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const Title = styled.h1`
	margin: 0;
	color: #303030;
	font-size: 38px;
	line-height: 1.25;
	font-weight: 700;

	@media (max-width: 700px) {
		font-size: 28px;
	}
`;

const DocumentInfo = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px 16px;
	font-weight: 600;
`;

const VersionBadge = styled.span`
	flex-shrink: 0;
	padding: 4px 12px;
	border-radius: 100px;
	background: #6936dd;
	color: #f3f5ff;
	font-size: 12px;
	font-weight: 500;
	line-height: normal;
`;

const CompanyLine = styled.span`
	color: #707070;
	font-weight: 400;
`;

const EffectiveDate = styled.p`
	margin: 0;
	color: #707070;
`;

const Divider = styled.hr`
	width: 100%;
	height: 1px;
	margin: 0;
	border: 0;
	background: #cdd4f6;
`;

const Section = styled.section`
	display: flex;
	flex-direction: column;
	gap: 16px;
	break-inside: avoid-page;
	page-break-inside: avoid;
`;

const SectionTitle = styled.h2`
	display: grid;
	grid-template-columns: 40px minmax(0, 1fr);
	gap: 16px;
	margin: 0;
	color: #3a0ca3;
	font-size: 14px;
	font-weight: 600;
	line-height: 24px;
`;

const SectionNumber = styled.span`
	width: 40px;
`;

const Nodes = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding-left: 56px;

	@media (max-width: 600px) {
		padding-left: 0;
	}
`;

const Clause = styled.div<{ $depth: number; $bullet: boolean }>`
	display: grid;
	grid-template-columns: ${({ $bullet }) => ($bullet ? "12px minmax(0, 1fr)" : "40px minmax(0, 1fr)")};
	align-items: flex-start;
	gap: ${({ $bullet }) => ($bullet ? "8px" : "16px")};
	padding-left: ${({ $depth }) => ($depth === 0 ? "0" : `${$depth * 48}px`)};
	color: ${({ $depth }) => ($depth > 0 ? "#707070" : "#303030")};
	break-inside: avoid-page;
	page-break-inside: avoid;

	@media (max-width: 600px) {
		padding-left: ${({ $depth }) => ($depth === 0 ? "0" : `${$depth * 24}px`)};
	}
`;

const Marker = styled.span<{ $depth: number; $bullet: boolean }>`
	width: ${({ $depth, $bullet }) => ($bullet ? "12px" : $depth === 0 ? "40px" : "32px")};
`;

const ClauseText = styled.span`
	flex: 1;
	min-width: 0;
`;

const INLINE_MARKUP_REGEX = /(\*\*|__)(.+?)\1/g;

const InlineText: React.FC<{ text: string }> = ({ text }) => {
	const parts: React.ReactNode[] = [];
	let lastIndex = 0;
	let match: RegExpExecArray | null;

	INLINE_MARKUP_REGEX.lastIndex = 0;
	while ((match = INLINE_MARKUP_REGEX.exec(text)) !== null) {
		if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
		parts.push(
			match[1] === "**" ? (
				<strong key={match.index}>{match[2]}</strong>
			) : (
				<NoticeLabel key={match.index}>{match[2]}</NoticeLabel>
			),
		);
		lastIndex = match.index + match[0].length;
	}
	if (lastIndex < text.length) parts.push(text.slice(lastIndex));

	return <>{parts}</>;
};

const renderNode = (node: TosNode, key: string, depth = 0): React.ReactNode => {
	if (node.groupLabel) {
		return (
			<Clause $depth={depth} $bullet={false} key={key}>
				<ClauseText>
					<strong>{node.groupLabel}</strong>
				</ClauseText>
			</Clause>
		);
	}

	const bullet = node.marker === "•";
	return (
		<React.Fragment key={key}>
			<Clause $depth={depth} $bullet={bullet}>
				{node.marker && (
					<Marker $depth={depth} $bullet={bullet} aria-hidden={bullet ? "true" : undefined}>
						{node.marker}
					</Marker>
				)}
				<ClauseText>
					<InlineText text={node.text ?? ""} />
				</ClauseText>
			</Clause>
			{node.children?.map((child, index) => renderNode(child, `${key}-${index}`, depth + 1))}
		</React.Fragment>
	);
};

interface TermsOfServiceBodyProps {
	fullPage?: boolean;
	printMode?: boolean;
	/** Rendered inside `LegalDocumentModal`, which owns the title block and scrolling. */
	embedded?: boolean;
}

const TermsOfServiceBody: React.FC<TermsOfServiceBodyProps> = ({
	fullPage = false,
	printMode = false,
	embedded = false,
}) => (
	<TermsContainer $fullPage={fullPage || embedded} $printMode={printMode}>
		<Document $embedded={embedded}>
			<DocumentHeader>
				{embedded ? (
					<Logo src="/Creatorverse_Wordmark_Purple.svg" alt="Creatorverse" />
				) : (
					<>
						<Logo src="/Creatorverse_Wordmark_Gradient.svg" alt="Creatorverse" />
						<Eyebrow>LEGAL DOCUMENT</Eyebrow>
					</>
				)}
			</DocumentHeader>
			<Preamble>
				<PreambleHeading>Preamble</PreambleHeading>
				{TOS_PREAMBLE.map((paragraph, index) => (
					<PreambleParagraph key={`preamble-${index}`}>
						<InlineText text={paragraph} />
					</PreambleParagraph>
				))}
				<Notice $embedded={embedded}>
					{TOS_PREAMBLE_NOTICE.map((paragraph, index) => (
						<p key={`notice-${index}`}>
							<InlineText text={paragraph} />
						</p>
					))}
				</Notice>
			</Preamble>
			{!embedded && (
				<>
					<TitleBlock>
						<Title>{TOS_DOCUMENT_META.title}</Title>
						<DocumentInfo>
							<VersionBadge>{TOS_DOCUMENT_META.displayVersion}</VersionBadge>
							<span>{TOS_DOCUMENT_META.companyName}</span>
							<CompanyLine>{TOS_DOCUMENT_META.companyLine}</CompanyLine>
						</DocumentInfo>
						<EffectiveDate>{TOS_DOCUMENT_META.effectiveLine}</EffectiveDate>
					</TitleBlock>
					<Divider />
				</>
			)}
			{TOS_SECTIONS.map((section) => (
				<Section key={section.number}>
					<SectionTitle>
						<SectionNumber aria-hidden="true">{section.number}.</SectionNumber>
						<span>{section.title}</span>
					</SectionTitle>
					<Nodes>{section.nodes.map((node, index) => renderNode(node, `${section.number}-${index}`))}</Nodes>
				</Section>
			))}
		</Document>
	</TermsContainer>
);

export default TermsOfServiceBody;
